<template>
  <div class="h-full overflow-y-auto p-4 md:p-8">
    <div class="max-w-4xl mx-auto">
      <h2 class="font-display text-3xl">Taste a wine</h2>
      <p class="text-sm text-ink-dim mt-1 max-w-2xl">
        Give me a bottle and I'll separate what the <span class="text-wine-bright">grape brings</span> from what
        <span class="text-gold-bright">place and craft</span> added — and show which varietal notes this wine
        actually expresses, and which stay quiet.
      </p>

      <!-- unified input: type, paste, drop or browse -->
      <div class="panel p-4 md:p-5 mt-6 transition-shadow" :class="dragOver ? 'ring-2 ring-gold/60' : ''"
        @dragover.prevent="dragOver = true" @dragleave.self="dragOver = false" @drop.prevent="onDrop" @paste="onPaste">
        <form @submit.prevent="analyze" class="flex flex-col sm:flex-row gap-2.5 sm:items-center">
          <input v-model="query"
            placeholder="Wine name or URL — or drop a label photo anywhere here"
            class="flex-1 bg-card border border-line rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-wine/60" />
          <div class="flex items-center gap-2 justify-end">
            <label class="cursor-pointer px-4 py-2.5 rounded-full border border-line text-sm text-ink-dim hover:text-ink shrink-0">
              📷 <span class="hidden sm:inline">Photo</span>
              <input type="file" accept="image/*" class="hidden" @change="onFile" />
            </label>
            <button :disabled="busy || !ready"
              class="px-5 py-2.5 rounded-full bg-gold/15 border border-gold/50 text-gold-bright text-sm disabled:opacity-40 shrink-0">
              {{ busy ? "Tasting…" : "Analyse" }}
            </button>
          </div>
        </form>

        <div v-if="image" class="mt-3 flex items-center gap-3">
          <img :src="image" class="h-16 rounded-lg border border-line" />
          <span class="text-xs text-ink-dim">{{ imageName }}</span>
          <button @click="clearImage" class="w-7 h-7 rounded-full border border-line grid place-items-center text-ink-faint hover:text-ink text-xs">✕</button>
        </div>

        <p class="text-xs text-ink-faint mt-3">
          Type a name, paste a URL, drag or paste a label photo — or combine a photo with a hint
          ("the 2019 one"). Photos work straight from the camera on your phone.
        </p>
        <p v-if="busy" class="text-sm text-ink-dim italic font-display mt-2 animate-pulse">
          swirling, sniffing, checking the cellar notes… (give it a minute)
        </p>
        <p v-if="error" class="text-sm text-wine-bright mt-2">{{ error }}</p>
      </div>

      <!-- result -->
      <template v-if="result">
        <div class="mt-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint">{{ result.wine.style }}</p>
            <h3 class="font-display text-3xl leading-tight">{{ result.wine.name }}</h3>
            <p class="text-sm text-ink-dim mt-1">
              {{ [result.wine.producer, result.wine.region, result.wine.country, result.wine.vintage].filter(Boolean).join(" · ") }}
            </p>
            <p v-if="result.wine.confidence !== 'identified'" class="text-xs text-gold-bright mt-1 italic">
              {{ result.wine.confidence_note }}
            </p>
          </div>
          <div class="flex flex-wrap gap-1.5">
            <button v-for="v in result.varietals" :key="v.name" @click="v.grape_slug && selectGrape(v.grape_slug)"
              class="chip wine text-sm !py-1.5 !px-3" :class="v.grape_slug ? 'cursor-pointer hover:bg-wine/15' : ''">
              {{ v.name }}<span v-if="v.percent" class="text-ink-faint ml-1.5">{{ v.percent }}%</span>
            </button>
          </div>
        </div>

        <p class="text-sm text-ink-dim leading-relaxed mt-4 max-w-3xl">{{ result.summary }}</p>

        <!-- legend -->
        <div class="panel px-4 py-3 mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-dim">
          <span class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint">How to read this</span>
          <span class="flex items-center gap-1.5"><span class="chip expressed">note</span> grape character, expressed here</span>
          <span class="flex items-center gap-1.5"><span class="chip amplified">note</span> amplified in this wine</span>
          <span class="flex items-center gap-1.5"><span class="chip muted">note</span> in the grape's repertoire, quiet here</span>
          <span class="flex items-center gap-1.5"><span class="chip crafted">note</span> from another layer — tagged vineyard / terroir / fruit state / ferment / élevage / age</span>
          <span class="flex items-center gap-1.5"><span class="chip faulty">note</span> fault territory (Brett, VA, TCA…)</span>
        </div>

        <!-- varietal expression -->
        <div class="mt-6">
          <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">What the grape brings — and what this wine does with it</p>
          <div class="flex flex-wrap gap-2">
            <span v-for="n in varietyNotes" :key="n.name" class="chip !text-[0.78rem] !py-1 !px-2.5"
              :class="n.status" :title="n.comment || ''">
              {{ n.name }}
            </span>
          </div>
        </div>

        <!-- the other seven layers -->
        <div class="mt-5" v-if="craftNotes.length">
          <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">
            What the other layers added — vineyard · terroir · fruit state · ferment · élevage · age
          </p>
          <div class="flex flex-wrap gap-2">
            <span v-for="n in craftNotes" :key="n.name" class="chip !text-[0.78rem] !py-1 !px-2.5"
              :class="n.source === 'fault' ? 'faulty' : 'crafted'" :title="n.comment || ''">
              {{ n.name }}<span class="src">{{ srcLabel(n.source) }}</span>
            </span>
          </div>
        </div>

        <!-- compounds -->
        <div class="mt-8" v-if="result.compounds?.length">
          <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-3">The chemistry in this glass</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div v-for="c in sortedCompounds" :key="c.name" class="panel px-4 py-3"
              :class="c.level === 'quiet' ? 'opacity-45' : ''"
              :style="c.source !== 'variety' && c.level !== 'quiet' ? 'border-color: rgba(201,162,39,.45)' : ''">
              <div class="flex items-baseline justify-between gap-2">
                <p class="text-sm">{{ compoundName(c) }}</p>
                <span class="text-[0.65rem] uppercase tracking-wider shrink-0"
                  :class="c.level === 'high' ? 'text-gold-bright' : c.level === 'quiet' ? 'text-ink-faint' : 'text-ink-dim'">
                  {{ c.level }} · {{ srcLabel(c.source) }}
                </span>
              </div>
              <p class="text-xs text-ink-dim mt-1">{{ c.comment }}</p>
            </div>
          </div>
        </div>

        <!-- layer-by-layer drill-down -->
        <div class="mt-10">
          <div class="flex items-baseline justify-between flex-wrap gap-2">
            <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint">This wine, layer by layer</p>
            <button @click="store.view = 'layers'" class="text-xs text-gold-bright hover:underline">what are the eight layers? →</button>
          </div>
          <div class="mt-3 flex flex-col gap-2.5">
            <details v-for="layer in layerBreakdown" :key="layer.id" open class="panel overflow-hidden group">
              <summary class="flex items-center gap-3 px-4 py-3 cursor-pointer list-none select-none">
                <span class="w-2.5 h-2.5 rounded-full shrink-0" :style="{ background: layer.color }" />
                <span class="font-display text-lg" :style="{ color: layer.color }">{{ layer.label }}</span>
                <span class="text-xs text-ink-faint">
                  {{ layer.notes.length }} note{{ layer.notes.length === 1 ? "" : "s" }}<template v-if="layer.compounds.length"> · {{ layer.compounds.length }} compound{{ layer.compounds.length === 1 ? "" : "s" }}</template>
                </span>
                <span class="ml-auto text-ink-faint text-sm group-open:rotate-90 transition-transform">›</span>
              </summary>
              <div class="px-4 pb-4 border-t border-line pt-3 flex flex-col gap-2">
                <div v-for="n in layer.notes" :key="'n' + n.name" class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span class="chip shrink-0 !text-[0.75rem]" :class="n.status || (layer.id === 'fault' ? 'faulty' : 'crafted')">{{ n.name }}</span>
                  <p class="text-xs text-ink-dim leading-relaxed flex-1 min-w-[12rem]">{{ n.comment }}</p>
                </div>
                <div v-for="c in layer.compounds" :key="'c' + c.name">
                  <p class="text-xs text-gold-bright/90 italic">{{ compoundName(c) }} <span class="not-italic text-ink-faint">({{ c.level }})</span></p>
                  <p class="text-xs text-ink-dim leading-relaxed mt-0.5">{{ c.comment }}</p>
                </div>
              </div>
            </details>
          </div>
        </div>

        <div class="mt-8 flex flex-wrap gap-2.5">
          <button @click="askAbout" class="px-5 py-2.5 rounded-full border border-gold/50 text-gold-bright text-sm hover:bg-gold/10">
            ✦ Discuss this wine with the sommelier
          </button>
          <button v-if="result.share_id" @click="share"
            class="px-5 py-2.5 rounded-full border border-line text-sm text-ink-dim hover:text-ink hover:border-wine/50">
            {{ shareCopied ? "✓ Link copied" : "⛓ Share this analysis" }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import { store, bySlug, selectGrape, askClaude } from "../store"
import { LAYERS, normaliseSource } from "../layers"

const query = ref(""), image = ref(null), imageName = ref("")
const busy = ref(false), error = ref(""), dragOver = ref(false)
const shareCopied = ref(false)

// The analysis lives in the store so it survives view switches and the
// sommelier chat can see what's on the page.
const result = computed({
  get: () => store.wineResult,
  set: v => { store.wineResult = v },
})

// Opened via a share link (/w/:id): load the saved analysis instead of
// running a new one.
onMounted(() => {
  if (!store.pendingShareId) return
  const id = store.pendingShareId
  store.pendingShareId = null
  loadShared(id)
})

async function loadShared(id) {
  busy.value = true
  try {
    const res = await fetch(`/api/wine/analysis/${id}`)
    if (res.ok) result.value = await res.json()
    else error.value = "That shared analysis doesn't exist — the link may be wrong."
  } catch {
    error.value = "Couldn't load the shared analysis — try again."
  } finally {
    busy.value = false
  }
}

async function share() {
  const url = `${location.origin}/w/${result.value.share_id}`
  if (navigator.share) {
    try { await navigator.share({ title: `${result.value.wine.name} — Ampelos`, url }); return } catch { return }
  }
  await navigator.clipboard.writeText(url)
  shareCopied.value = true
  setTimeout(() => (shareCopied.value = false), 2500)
}

const ready = computed(() => query.value.trim() || !!image.value)

// Downscale label photos client-side: phone photos are many MB, and the
// vision API caps at 8000px / 5MB. 1600px JPEG is plenty to read a label;
// step down further if the result is somehow still heavy.
function processFile(f) {
  if (!f || !f.type.startsWith("image/")) return
  imageName.value = f.name || "pasted image"
  const img = new Image()
  const blobUrl = URL.createObjectURL(f)
  img.onload = () => {
    const MAX_DATAURL = 3_500_000 // ~2.6MB decoded, well under the API's 5MB
    let out = null
    for (const [max, quality] of [[1600, 0.85], [1280, 0.75], [1024, 0.65], [800, 0.55]]) {
      const scale = Math.min(1, max / Math.max(img.width, img.height))
      const canvas = document.createElement("canvas")
      canvas.width = Math.max(1, Math.round(img.width * scale))
      canvas.height = Math.max(1, Math.round(img.height * scale))
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height)
      out = canvas.toDataURL("image/jpeg", quality)
      if (out.length <= MAX_DATAURL) break
    }
    URL.revokeObjectURL(blobUrl)
    if (out.length > MAX_DATAURL) {
      error.value = "That image won't compress small enough — try a tighter photo of the label."
      return
    }
    image.value = out
  }
  img.onerror = () => { error.value = "Couldn't read that image — try another photo."; URL.revokeObjectURL(blobUrl) }
  img.src = blobUrl
}

function onFile(e) { processFile(e.target.files[0]); e.target.value = "" }

function onDrop(e) {
  dragOver.value = false
  const f = [...(e.dataTransfer.files || [])].find(x => x.type.startsWith("image/"))
  if (f) return processFile(f)
  const uri = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain")
  if (uri) query.value = uri.trim()
}

function onPaste(e) {
  const item = [...(e.clipboardData?.items || [])].find(x => x.type.startsWith("image/"))
  if (item) { e.preventDefault(); processFile(item.getAsFile()) }
}

function clearImage() { image.value = null; imageName.value = "" }

async function analyze() {
  busy.value = true
  error.value = ""
  result.value = null
  try {
    const body = { query: query.value.trim() || null, image: image.value }
    const res = await fetch("/api/wine/analyze", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
    const data = await res.json()
    if (res.status === 503) error.value = "No Anthropic API key configured yet — add it to .env and restart."
    else if (res.status === 413) error.value = `Image too large — ${data.error_message}.`
    else if (!res.ok) error.value = `Analysis failed (${data.error || res.status}).`
    else {
      result.value = data
      if (data.share_id) history.replaceState(null, "", `/w/${data.share_id}`)
    }
  } catch {
    error.value = "The connection was interrupted mid-analysis — try again."
  } finally {
    busy.value = false
  }
}

// Varietal repertoire notes: Claude's classification, plus any repertoire
// descriptors it didn't mention, shown muted (belt and braces).
const varietyNotes = computed(() => {
  if (!result.value) return []
  const notes = (result.value.notes || []).filter(n => n.source === "variety")
  const covered = new Set(notes.map(n => n.descriptor_slug).filter(Boolean))
  const slugs = (result.value.varietals || []).map(v => v.grape_slug).filter(Boolean)
  for (const s of slugs) {
    for (const d of bySlug.value.get(s)?.descriptors || []) {
      if (!covered.has(d)) {
        covered.add(d)
        notes.push({ descriptor_slug: d, name: pretty(d), status: "muted", source: "variety", comment: "In the variety's repertoire; not prominent in this wine." })
      }
    }
  }
  const order = { amplified: 0, expressed: 1, muted: 2 }
  return notes.sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3))
})

const SOURCE_ORDER = ["vineyard", "terroir", "fruit-state", "ferment", "elevage", "age", "fault"]
const craftNotes = computed(() =>
  (result.value?.notes || [])
    .filter(n => n.source !== "variety")
    .sort((a, b) => SOURCE_ORDER.indexOf(a.source) - SOURCE_ORDER.indexOf(b.source)))

// Group every note and compound under the layer that wrote it. The variety
// layer reuses the full repertoire classification (including inferred muted).
const layerBreakdown = computed(() => {
  if (!result.value) return []
  return LAYERS.map(l => {
    const notes = l.id === "variety"
      ? varietyNotes.value
      : (result.value.notes || []).filter(n => normaliseSource(n.source) === l.id)
    const compounds = (result.value.compounds || []).filter(c => normaliseSource(c.source) === l.id)
    return { ...l, notes, compounds }
  }).filter(l => l.notes.length || l.compounds.length)
})

const sortedCompounds = computed(() => {
  const order = { high: 0, present: 1, quiet: 2 }
  return [...(result.value?.compounds || [])].sort((a, b) => (order[a.level] ?? 3) - (order[b.level] ?? 3))
})

function compoundName(c) {
  const known = c.compound_slug && store.compounds.find(x => x.slug === c.compound_slug)
  return known ? known.name : c.name
}

const srcLabels = {
  variety: "grape", vineyard: "vineyard", terroir: "terroir", "fruit-state": "fruit state",
  ferment: "ferment", elevage: "élevage", age: "age", fault: "fault",
  // legacy values, in case the model emits the old enum
  oak: "élevage", fermentation: "ferment", aging: "age",
}
const srcLabel = s => srcLabels[s] || s
const pretty = s => s.replace(/-/g, " ")

function askAbout() {
  if (!result.value) return
  askClaude(`We just analysed ${result.value.wine.name} in the Taste-a-wine view. Tell me more about how this producer's choices shape what the ${result.value.varietals.map(v => v.name).join("/")} shows here.`)
}
</script>

<style scoped>
.chip.expressed {
  border-color: rgba(217, 99, 111, 0.65);
  background: rgba(176, 58, 72, 0.22);
  color: #f4eadc;
}
.chip.amplified {
  border-color: rgba(230, 198, 92, 0.8);
  background: rgba(176, 58, 72, 0.25);
  color: #f4eadc;
  box-shadow: 0 0 8px rgba(230, 198, 92, 0.35);
}
.chip.muted {
  border-style: dashed;
  border-color: rgba(179, 156, 140, 0.35);
  background: transparent;
  color: #7d6a5f;
}
.chip.crafted {
  border-color: rgba(201, 162, 39, 0.5);
  background: rgba(201, 162, 39, 0.10);
  color: #e6c65c;
}
.chip.faulty {
  border-color: rgba(162, 54, 42, 0.7);
  background: rgba(162, 54, 42, 0.16);
  color: #d9636f;
}
.chip .src {
  margin-left: 0.45rem;
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.7;
}
</style>
